// Pure detection helpers for the "Connect your agent" overlay. Kept separate
// from the dialog so the matching logic is unit-tested without rendering React.

export interface ChannelLike {
  id: string;
  name?: string | null;
}

/**
 * Find the workspace's #general channel from a channel list. Matches the
 * conventional name case-insensitively — the workspace bootstrap always
 * creates a channel literally named "general". Returns null when absent
 * (sync lag, or a workspace whose general was renamed/removed), in which case
 * the finale step stays disabled rather than targeting the wrong channel.
 */
export function findGeneralChannel<T extends ChannelLike>(
  channels: readonly T[] | undefined | null,
): T | null {
  if (!channels) return null;
  return (
    channels.find((c) => (c.name ?? "").toLowerCase() === "general") ?? null
  );
}

export interface DetectableMessage {
  id: string;
  // The messages table stores the sender as `user_id` (there is NO
  // `sender_id` column). The Ano CLI authenticates AS the user, so the
  // agent's post lands with `user_id === <the user>`.
  user_id?: string | null;
}

/**
 * Detect the user's own agent posting into #general after the finale ran.
 *
 * Rather than compare timestamps (fragile to client/server clock skew, and
 * prone to false-celebrating on a message the user typed shortly before
 * clicking), the caller snapshots the message ids already present at the
 * moment the finale fires (`seenIds`). A post counts only if it is from the
 * user AND was not in that snapshot — i.e. genuinely new. Returns the matching
 * message (newest first, as delivered DESC) or null.
 */
export function detectAgentMessage(
  messages: readonly DetectableMessage[] | undefined | null,
  seenIds: ReadonlySet<string>,
  userId: string,
): DetectableMessage | null {
  if (!messages) return null;
  return (
    messages.find((m) => m.user_id === userId && !seenIds.has(m.id)) ?? null
  );
}
